import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  buildLocationMarkers,
  buildMachineMarkers,
  buildAlertMarkers,
} from "@/map/utils/map-coordinates";
import { useNewAlertIds } from "@/alerts/stores/use-alert-notify-store";
import type { Alert, AlertStatus } from "@/alerts/types/machine-alert";
import type { CoordinateSet } from "@/map/types/map";
import type { TLocation } from "@/locations/types/location.ts";
import type { Machine } from "@/machines/types/machine.ts";

interface UseMapMarkersArgs {
  view: "org" | "location";
  locations: TLocation[];
  machines: Machine[];
  alerts: Alert[];
  /** Empty means "every status" — otherwise only these appear on the map. */
  statusFilter: AlertStatus[];
  selectedAlertId: string | null;
  /** Selected machine's GPS trail, only while the GPS panel is open. */
  gpsTrailCoords: CoordinateSet[];
  isError: boolean;
  isLoading: boolean;
}

/**
 * Everything the map draws, plus the notice shown over it.
 *
 * The org view plots one pin per location; the location view plots every
 * machine at that location AND every geo-located alert across those machines
 * (never gated to the selected machine), with the GPS trail appended when its
 * panel is open.
 */
export const useMapMarkers = ({
  view,
  locations,
  machines,
  alerts,
  statusFilter,
  selectedAlertId,
  gpsTrailCoords,
  isError,
  isLoading,
}: UseMapMarkersArgs) => {
  const { t } = useTranslation("map");

  // The status filter drives the on-map markers as well as the feed. Empty
  // selection shows every status.
  const visibleAlerts = useMemo(
    () =>
      statusFilter.length === 0 ? alerts : alerts.filter((a) => statusFilter.includes(a.status)),
    [alerts, statusFilter],
  );

  // Freshly-arrived, still-unresolved alert ids — drawn distinct + highlighted.
  // Persisted, and only cleared when resolved.
  const newAlertIds = useNewAlertIds();

  const coordinates = useMemo(() => {
    if (view === "org") return buildLocationMarkers(locations);
    const withAlerts = [
      ...buildMachineMarkers(machines),
      ...buildAlertMarkers(visibleAlerts, selectedAlertId, newAlertIds),
    ];
    return gpsTrailCoords.length > 0 ? [...withAlerts, ...gpsTrailCoords] : withAlerts;
  }, [view, locations, machines, visibleAlerts, gpsTrailCoords, selectedAlertId, newAlertIds]);

  // The map tiles stay visible at all times; this drives a small overlay alert
  // rather than a full-screen takeover when there is nothing to plot.
  const notice = isError
    ? t("notice.error")
    : coordinates.length === 0 && isLoading
      ? t("notice.loading")
      : coordinates.length === 0
        ? t("notice.noGps")
        : null;

  return { coordinates, visibleAlerts, newAlertIds, notice };
};
